// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0
// Licensed under the Apache License, Version 2.0 (https://www.apache.org/licenses/LICENSE-2.0).

// Run history (requirement 6). Set runs show the set name and "Version N" or "Draft set"; set previews show
// "(set preview)"; packet suggestions show "(set proposal)" with a link to the proposal (blueprint-sets Req 4.10, 11.4).
import { useState } from 'react';
import { useNavigate } from 'react-router';
import Box from '@cloudscape-design/components/box';
import Button from '@cloudscape-design/components/button';
import ButtonDropdown from '@cloudscape-design/components/button-dropdown';
import DateRangePicker, { type DateRangePickerProps } from '@cloudscape-design/components/date-range-picker';
import Header from '@cloudscape-design/components/header';
import Link from '@cloudscape-design/components/link';
import Select from '@cloudscape-design/components/select';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Table from '@cloudscape-design/components/table';
import { SET_PREVIEW_BLUEPRINT_NAME, type Run } from '@bda/shared';
import { api } from '../../api/client';
import { useBlueprints, useRuns } from '../../api/hooks';
import { setsEnabled } from '../../config';
import { formatDate, formatDuration, runModeLabel, versionLabel } from '../../lib/format';
import { errorMessage, useNotify } from '../../lib/notify';
import { RunStatusIndicator, setVersionLabel } from './RunResultPage';

export const SET_PROPOSAL_NAME = '(set proposal)';

/** Where a history row opens: a packet suggestion opens its proposal while sets are on, every other run its result. */
export const runPath = (r: Pick<Run, 'id' | 'mode'>): string =>
  r.mode === 'packetSuggest' && setsEnabled() ? `/sets/proposals/${encodeURIComponent(r.id)}` : `/runs/${r.id}`;

/** The Blueprint column's text: the set name for set runs, fixed names for set previews and packet suggestions. */
export function runTargetName(r: Pick<Run, 'mode' | 'blueprintName' | 'setName'>): string {
  if (r.mode === 'set') return r.setName ?? r.blueprintName;
  if (r.mode === 'setPreview') return SET_PREVIEW_BLUEPRINT_NAME;
  if (r.mode === 'packetSuggest') return SET_PROPOSAL_NAME;
  return r.blueprintName;
}

/** The Version column: "Version N" or "Draft set" for set runs, as before for everything else. */
export const runVersionText = (r: Pick<Run, 'mode' | 'version' | 'setVersion'>): string =>
  r.mode === 'set'
    ? setVersionLabel(r.setVersion)
    : r.mode === 'setPreview' || r.mode === 'packetSuggest'
      ? '-'
      : versionLabel(r.version);

const STATUSES = ['QUEUED', 'PROCESSING', 'SUCCEEDED', 'FAILED'] as const;

export function rangeToDates(
  range: DateRangePickerProps.Value | null,
  now = new Date(),
): { from?: string; to?: string } {
  if (!range) return {};
  if (range.type === 'absolute') return { from: range.startDate.slice(0, 10), to: range.endDate.slice(0, 10) };
  const unitDays = { day: 1, week: 7, month: 30, year: 365, second: 0, minute: 0, hour: 0 }[range.unit] ?? 1;
  const from = new Date(now.getTime() - range.amount * unitDays * 86_400_000);
  return { from: from.toISOString().slice(0, 10), to: now.toISOString().slice(0, 10) };
}

export function HistoryPage() {
  const navigate = useNavigate();
  const notify = useNotify();
  const [blueprintId, setBlueprintId] = useState<string | undefined>();
  const [status, setStatus] = useState<string | undefined>();
  const [range, setRange] = useState<DateRangePickerProps.Value | null>(null);
  const [cursors, setCursors] = useState<Array<string | undefined>>([undefined]);
  const page = cursors.length - 1;
  const bps = useBlueprints({});
  const runs = useRuns({ blueprintId, status, ...rangeToDates(range), cursor: cursors[page] });

  const resetPaging = () => setCursors([undefined]);
  const act = async (r: Run, action: string) => {
    if (action === 'open') return navigate(runPath(r));
    if (action === 'blueprint' && r.blueprintId) return navigate(`/blueprints/${r.blueprintId}`);
    if (action === 'package' && r.blueprintId)
      return navigate(`/packages/new?blueprintId=${r.blueprintId}&version=${r.version}&sample=${r.id}`);
    if (action === 'rerun') {
      try {
        const next = await api.retryRun(r.id);
        navigate(`/runs/${next.id}`);
      } catch (e) {
        notify.error(errorMessage(e), e);
      }
    }
  };

  const bpOptions = [
    { value: '', label: 'All blueprints' },
    ...(bps.data?.items ?? []).map((b) => ({ value: b.id, label: b.name })),
  ];
  const statusOptions = [
    { value: '', label: 'Any status' },
    ...STATUSES.map((s) => ({ value: s, label: s[0] + s.slice(1).toLowerCase() })),
  ];

  return (
    <Table
      header={<Header variant="h1">History</Header>}
      loading={runs.isLoading}
      loadingText="Loading runs"
      items={runs.data?.items ?? []}
      trackBy="id"
      filter={
        <SpaceBetween direction="horizontal" size="m">
          <Select
            ariaLabel="Filter by blueprint"
            selectedOption={bpOptions.find((o) => o.value === (blueprintId ?? '')) ?? bpOptions[0]!}
            options={bpOptions}
            onChange={(e) => {
              setBlueprintId(e.detail.selectedOption.value || undefined);
              resetPaging();
            }}
          />
          <Select
            ariaLabel="Filter by status"
            selectedOption={statusOptions.find((o) => o.value === (status ?? '')) ?? statusOptions[0]!}
            options={statusOptions}
            onChange={(e) => {
              setStatus(e.detail.selectedOption.value || undefined);
              resetPaging();
            }}
          />
          <DateRangePicker
            value={range}
            onChange={(e) => {
              setRange(e.detail.value);
              resetPaging();
            }}
            relativeOptions={[
              { key: '7d', amount: 7, unit: 'day', type: 'relative' },
              { key: '30d', amount: 30, unit: 'day', type: 'relative' },
              { key: '90d', amount: 90, unit: 'day', type: 'relative' },
            ]}
            isValidRange={() => ({ valid: true })}
            dateOnly
            placeholder="Any date"
            i18nStrings={{ ariaLabel: 'Filter by date range' }}
          />
        </SpaceBetween>
      }
      pagination={
        <SpaceBetween direction="horizontal" size="xs">
          <Button disabled={page === 0} onClick={() => setCursors(cursors.slice(0, -1))}>
            Previous
          </Button>
          <Button disabled={!runs.data?.nextCursor} onClick={() => setCursors([...cursors, runs.data!.nextCursor!])}>
            Next
          </Button>
        </SpaceBetween>
      }
      empty={<Box textAlign="center">No runs match these filters.</Box>}
      columnDefinitions={[
        {
          id: 'doc',
          header: 'Document',
          isRowHeader: true,
          cell: (r) => (
            <Link
              href={runPath(r)}
              onFollow={(e) => {
                e.preventDefault();
                navigate(runPath(r));
              }}
            >
              {r.fileName}
            </Link>
          ),
        },
        {
          id: 'bp',
          header: 'Blueprint',
          cell: (r) =>
            r.mode === 'packetSuggest' && setsEnabled() ? (
              <Link
                href={runPath(r)}
                onFollow={(e) => {
                  e.preventDefault();
                  navigate(runPath(r));
                }}
              >
                {SET_PROPOSAL_NAME}
              </Link>
            ) : (
              runTargetName(r)
            ),
        },
        { id: 'mode', header: 'Type', cell: (r) => runModeLabel(r.mode) },
        { id: 'version', header: 'Version', cell: (r) => runVersionText(r) },
        {
          id: 'status',
          header: 'Status',
          // An expired run keeps its status next to the notice (requirement 6.5).
          cell: (r) =>
            r.expired ? (
              <SpaceBetween direction="horizontal" size="xs">
                <RunStatusIndicator status={r.status} />
                <Box variant="span" color="text-body-secondary">
                  Document and result expired
                </Box>
              </SpaceBetween>
            ) : (
              <RunStatusIndicator status={r.status} />
            ),
        },
        { id: 'started', header: 'Started', cell: (r) => formatDate(r.startedAt) },
        { id: 'duration', header: 'Duration', cell: (r) => formatDuration(r.durationMs) },
        {
          id: 'actions',
          header: 'Actions',
          cell: (r) => (
            <ButtonDropdown
              variant="inline-icon"
              ariaLabel={`Actions for ${r.fileName}`}
              items={[
                { id: 'open', text: 'Open' },
                { id: 'rerun', text: 'Re-run', disabled: r.expired || !['SUCCEEDED', 'FAILED'].includes(r.status) },
                { id: 'blueprint', text: 'Open blueprint', disabled: !r.blueprintId },
                {
                  id: 'package',
                  text: 'Create handoff package',
                  disabled: r.status !== 'SUCCEEDED' || typeof r.version !== 'number',
                },
              ]}
              onItemClick={(e) => void act(r, e.detail.id)}
            />
          ),
        },
      ]}
    />
  );
}
